/**
 * biometri-native.js
 *
 * Wrapper om @capgo/capacitor-native-biometric til brug i SkilsmisseVejen Capacitor APK.
 *
 * Arkitektur:
 *   - Virker KUN i native Android-kontekst (Capacitor.isNativePlatform())
 *   - window.PublicKeyCredential er undefined i Capacitor WebView — dette er løsningen
 *   - Gemmer krypterede credentials (email + adgangskode) i Android Keystore
 *   - Fingeraftryk låser op for credentials → normal login-flow genbruges uændret
 *   - Backend passkey.js og SimpleWebAuthn-serveren berøres ikke
 *
 * APK-kompatibilitet:
 *   - erNativeBiometriTilgaengelig() er en hurtig sync platform-check (ikke plugin-check).
 *     Den returnerer true på alle native Android-builds — også ældre APKs uden plugin.
 *   - Brug tjekBiometriskMedPluginGuard() for at verificere at plugin faktisk er installeret.
 *     Denne funktion bruges i useEffect i Login.jsx og er den eneste korrekte indgang
 *     til native biometri-logik.
 *
 * API:
 *   erNativeBiometriTilgaengelig()          → boolean (sync platform-check — IKKE plugin-check)
 *   tjekBiometriskMedPluginGuard()          → Promise<{ tilgaengelig: boolean, type: number }>
 *   tjekBiometrisk()                        → Promise<{ tilgaengelig, type }>
 *   gemCredentialsMedBiometri(email, pw)    → Promise<void>
 *   hentCredentialsMedBiometri()            → Promise<{ email, adgangskode }>
 *   sletGemteCredentials()                  → Promise<void>
 *   harGemteCredentials()                   → Promise<boolean>
 */

// Server-nøgle brugt til at identificere credentials i Android Keystore
const BIOMETRI_SERVER = 'app.skilsmissevejen.dk';

/**
 * Hurtig sync-check: er vi på native Android?
 * Bruger window.Capacitor (runtime) i stedet for statisk @capacitor/core import,
 * så web-buildet ikke fejler ved manglende pakke.
 *
 * OBS: Returnerer true på alle native Android Capacitor-builds — også APKs hvor
 * @capgo/capacitor-native-biometric IKKE er installeret (fx v1.2.0).
 * Brug aldrig denne funktion alene til at afgøre om biometri-flow skal aktiveres.
 * Brug tjekBiometriskMedPluginGuard() i stedet.
 */
export function erNativeBiometriTilgaengelig() {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.Capacitor &&
      window.Capacitor.isNativePlatform() === true &&
      window.Capacitor.getPlatform() === 'android'
    );
  } catch {
    return false;
  }
}

/**
 * Hent NativeBiometric plugin dynamisk — undgår fejl i web-kontekst.
 * Kaster PluginNotInstalledError hvis @capgo/capacitor-native-biometric
 * ikke er registreret i den aktuelle APK (fx v1.2.0).
 * @returns {Promise<import('@capgo/capacitor-native-biometric').NativeBiometricPlugin>}
 * @throws {Error} hvis plugin ikke er tilgængeligt
 */
async function hent_plugin() {
  try {
    const mod = await import('@capgo/capacitor-native-biometric');
    if (!mod || !mod.NativeBiometric) {
      throw new Error('NativeBiometric plugin ikke tilgængeligt i denne APK');
    }
    return mod.NativeBiometric;
  } catch (err) {
    // Inkluderer både manglende modul og Capacitor bridge-fejl
    throw new Error('NativeBiometric plugin ikke tilgængeligt: ' + (err?.message || err));
  }
}

/**
 * Kombineret async check: er vi på native Android OG er plugin'et installeret?
 * Dette er den sikre indgang der bruges i Login.jsx's useEffect.
 * Returnerer altid { tilgaengelig: false } ved enhver fejl — crasher aldrig.
 * @returns {Promise<{ tilgaengelig: boolean, type: number, fejlkode?: number }>}
 */
export async function tjekBiometriskMedPluginGuard() {
  if (!erNativeBiometriTilgaengelig()) {
    return { tilgaengelig: false, type: 0 };
  }
  try {
    const NativeBiometric = await hent_plugin();
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    return {
      tilgaengelig: result.isAvailable,
      type: result.biometryType,
      fejlkode: result.errorCode,
    };
  } catch {
    // Plugin ikke installeret i denne APK, eller biometri ikke konfigureret
    return { tilgaengelig: false, type: 0 };
  }
}

/**
 * Tjek om fingeraftryk / ansigtsgenkendelse er tilgængeligt og konfigureret.
 * Forudsætter at plugin er tilgængeligt — bruges internt og i fuldfør_login i Login.jsx.
 * For den sikre indgang med plugin-guard: brug tjekBiometriskMedPluginGuard().
 * @returns {Promise<{ tilgaengelig: boolean, type: number, fejlkode?: number }>}
 */
export async function tjekBiometrisk() {
  if (!erNativeBiometriTilgaengelig()) {
    return { tilgaengelig: false, type: 0 };
  }
  try {
    const NativeBiometric = await hent_plugin();
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    return {
      tilgaengelig: result.isAvailable,
      type: result.biometryType,
      fejlkode: result.errorCode,
    };
  } catch {
    // Fanger plugin-mangl (v1.2.0 APK) og biometri-konfigurationsfejl
    return { tilgaengelig: false, type: 0 };
  }
}

/**
 * Gem email + adgangskode krypteret i Android Keystore.
 * Biometrisk bekræftelse kræves ikke ved gem — kun ved hentning.
 * @param {string} email
 * @param {string} adgangskode
 */
export async function gemCredentialsMedBiometri(email, adgangskode) {
  if (!erNativeBiometriTilgaengelig()) throw new Error('Native biometri ikke tilgængelig');
  const NativeBiometric = await hent_plugin();
  await NativeBiometric.setCredentials({
    username: email,
    password: adgangskode,
    server: BIOMETRI_SERVER,
  });
}

/**
 * Hent gemte credentials — viser fingeraftryk-prompt til brugeren.
 * Kaster fejl hvis brugeren annullerer eller autentificering fejler.
 * @returns {Promise<{ email: string, adgangskode: string }>}
 */
export async function hentCredentialsMedBiometri() {
  if (!erNativeBiometriTilgaengelig()) throw new Error('Native biometri ikke tilgængelig');
  const NativeBiometric = await hent_plugin();

  // Trin 1: Biometrisk bekræftelse
  await NativeBiometric.verifyIdentity({
    reason: 'Log ind på SkilsmisseVejen',
    title: 'Fingeraftryk',
    subtitle: 'Bekræft din identitet',
    description: 'Brug fingeraftryk eller ansigtsscanning for at logge ind',
    negativeButtonText: 'Annuller',
    useFallback: false,
    maxAttempts: 3,
  });

  // Trin 2: Hent krypterede credentials fra Keystore
  const creds = await NativeBiometric.getCredentials({ server: BIOMETRI_SERVER });
  return { email: creds.username, adgangskode: creds.password };
}

/**
 * Slet gemte credentials fra Android Keystore.
 */
export async function sletGemteCredentials() {
  if (!erNativeBiometriTilgaengelig()) return;
  const NativeBiometric = await hent_plugin();
  await NativeBiometric.deleteCredentials({ server: BIOMETRI_SERVER });
}

/**
 * Tjek om der eksisterer gemte credentials (uden at vise biometri-prompt).
 * Bruges til at afgøre om fingeraftryk-login-knappen skal vises.
 * @returns {Promise<boolean>}
 */
export async function harGemteCredentials() {
  if (!erNativeBiometriTilgaengelig()) return false;
  try {
    const NativeBiometric = await hent_plugin();
    const creds = await NativeBiometric.getCredentials({ server: BIOMETRI_SERVER });
    return !!(creds && creds.username);
  } catch {
    // getCredentials kaster fejl hvis ingen credentials er gemt
    return false;
  }
}
